import { config } from "./constants";
import { FWFunction } from "./error-handler-util";
import ClientUtil from "../client-util";

import mixpanel, { Config } from "mixpanel-browser";

export interface MixpanelDetail {
  functionName: FWFunction;
  status: "success" | "failure";
  cellAddress?: string;
  error?: string;
}

export type Client = "excel" | "gsheets";

export default class MixpanelTracker {
  static initiated: boolean = false;

  static getUserEmail() {
    const userDetails = ClientUtil.getTokenDetails();
    const userEmail = userDetails?.email || userDetails?.preferred_username || localStorage.getItem("useremail");

    return userEmail;
  }

  static init() {
    if (this.initiated) return;

    const userEmail = MixpanelTracker.getUserEmail();

    const mixpanelOptions: Partial<Config> = { ip: false };
    if (config.mixpanelApiHost) {
      mixpanelOptions.api_host = config.mixpanelApiHost;
      mixpanelOptions.api_routes = {
        track: "kcart/",
        engage: "egagne/",
        groups: "groups/"
      };
    }

    mixpanel.init(config.mixpanelId, mixpanelOptions);
    mixpanel.identify(userEmail);
    this.initiated = true;
  }

  static trackFunction(detail: MixpanelDetail & { actor?: string }) {
    if (!detail) return;

    detail.actor = MixpanelTracker.getUserEmail();

    if (localStorage.getItem("client") === "gsheets") {
      console.log(detail);

      MixpanelTracker.#cleanLocalMixpanel();
      return;
    };

    const now = Date.now();
    localStorage.setItem("eventTimeStamp", now.toString());

    const recording = localStorage.getItem("recordingEvents") === "true";
    if (!recording) {
      localStorage.setItem("recordingEvents", "true");
      setTimeout(() => MixpanelTracker.batchMultipleEvents(), 60 * 1000);
    }

    const details = localStorage.getItem("eventDetails") || "";
    localStorage.setItem("eventDetails", `${details}${details === "" ? "" : "&"}${JSON.stringify(detail)}`);
  }

  static batchMultipleEvents() {
    if (localStorage.getItem("client") === "gsheets") { // TEMPORARILY DISABLE MIXPANEL EVENTS TRACKING ON GSHEETS
      console.log("batchMultipleEvents called - Skipping Mixpanel Event Tracking on GSHEETS");

      MixpanelTracker.#cleanLocalMixpanel();
      return;
    };

    try {
      let groupedDetails = {} as Record<FWFunction, any>;

      const details = MixpanelTracker.parseEventDetails<MixpanelDetail>();
      if (!Boolean(details?.length)) return;

      details.forEach((detail) => {
        groupedDetails = MixpanelTracker.#upsertKeyInObj(groupedDetails, detail.functionName);
        const functionGroupedDetails = MixpanelTracker.#groupEachDetail(groupedDetails, detail);
        groupedDetails[detail.functionName] = functionGroupedDetails;
      });

      Object.keys(groupedDetails).forEach((functionName) => {
        const successEvents = groupedDetails[functionName].success;
        const failureEvents = groupedDetails[functionName].failure;

        const eventTimeDiff = (Date.now() - parseInt(localStorage.getItem("eventTimeStamp") || "0")) / 1000;

        MixpanelTracker.#trackSuccessEvents(successEvents, functionName, eventTimeDiff);
        MixpanelTracker.#trackFailureEvents(failureEvents, functionName, eventTimeDiff);
      });
    } catch (err) {
      console.error(err);
      console.log("Couldn't record event");
    } finally {
      MixpanelTracker.#cleanLocalMixpanel();
    }
  }

  static #groupEachDetail(groupedDetails: Record<FWFunction, any>, detail: MixpanelDetail) {
    let functionGroupedDetails = groupedDetails[detail.functionName];

    functionGroupedDetails = MixpanelTracker.#upsertKeyInObj(functionGroupedDetails, detail.status, detail.status === "failure" ? {} : []);

    if (detail.status === "success") {
      functionGroupedDetails.success.push(detail.cellAddress);
    } else {
      if (!detail.error) return groupedDetails;

      detail.error = JSON.stringify(detail.error);

      functionGroupedDetails.failure = MixpanelTracker.#upsertKeyInObj(functionGroupedDetails.failure, `${detail.error}`, []);
      functionGroupedDetails.failure[detail.error].push(detail.cellAddress);
    }

    return functionGroupedDetails;
  }

  static #trackSuccessEvents(successEvents: Array<string>, functionName: string, eventTimeDiff: number) {
    if (!Boolean(successEvents?.length)) return;
    MixpanelTracker.trackEvent(`FW.${functionName}`, {
      "status": "success",
      "count": successEvents.length,
      "cellAddress": successEvents,
      "timeDifference(in seconds)": eventTimeDiff
    });
  }

  static #trackFailureEvents(failureEvents: { error: Array<string> }, functionName: string, eventTimeDiff: number) {
    if (!failureEvents) return;
    Object.keys(failureEvents)?.forEach((error) => {
      MixpanelTracker.trackEvent(`FW.${functionName}`, {
        "status": "failure",
        "error": error,
        "count": failureEvents[error].length,
        "cellAddress": failureEvents[error],
        "timeDifference(in seconds)": eventTimeDiff
      });
    });
  }

  static #cleanLocalMixpanel() {
    localStorage.removeItem("eventDetails");
    localStorage.setItem("recordingEvents", "false");
  }

  static #upsertKeyInObj<T>(object: Record<string, T>, key: string, defaultValue?: any) {
    if (!object) return object;

    if (!(key in object)) {
      object[key] = defaultValue || {};
    }

    return object;
  }

  static parseEventDetails<T>() {
    try {
      const details: Array<T> =
        localStorage
          .getItem("eventDetails")
          ?.split("&")
          ?.map((json: string) => JSON.parse(json)) || [];
      return details;
    } catch (err) {
      return [];
    }
  }

  static trackEvent(event: string, details: any) {
    details.client = localStorage.getItem("client");
    details.instanceUrl = localStorage.getItem("team");
    details.dealflowTeamName = localStorage.getItem("dealflowTeamName");
    details.workbookName = localStorage.getItem("workbook");
    details.fw_version = localStorage.getItem("fw_version");

    details.actor = MixpanelTracker.getUserEmail();

    const metadata = localStorage.getItem("metadata"); 
    if (!!metadata) details = Object.assign(details, JSON.parse(metadata)); 

    if (localStorage.getItem("client") === "gsheets") {
      console.log(event, details);
      MixpanelTracker.#cleanLocalMixpanel();

      return;
    }

    MixpanelTracker.init();

    return mixpanel.track(event, details);
  }
}
